import { Check, Copy, Mail, Send } from "lucide-react";
import { useState } from "react";

import type { UC5Job } from "../../lib/api";
import type { Me } from "../../lib/me";
import { copyDraft, draftOutreach, markOutreachSent, type OutreachDraft } from "../../pages/vendorFlow";
import { Explainer } from "../Explainer";
import { RoleButton } from "../RequireRole";

/** An ISO timestamp as its date, which is what a reply deadline is about. */
export function day(iso: string | null | undefined): string {
  return iso ? iso.slice(0, 10) : "";
}

/**
 * The vendor enquiry: the draft to send from your own mailbox, then the record that it went.
 * Pure, so every state renders without a network.
 */
export function OutreachView({ job, me, draft, busy, error, copied, onDraft, onCopy, onSent }: {
  job: UC5Job; me: Me | null | undefined; draft: OutreachDraft | null; busy: boolean;
  error: string | null; copied: boolean;
  onDraft: () => void; onCopy: () => void; onSent: () => void;
}) {
  const sent = Boolean(job.outreach_sent_at);
  return (
    <div className="card">
      <div className="section-title">
        <span className="stage-chip">Enquiry</span><h3 className="row"><Mail size={18} /> Vendor enquiry</h3>
      </div>
      <Explainer>
        A lifecycle enquiry to the vendor, written from the approved specification and the sources
        cited. This system sends nothing: copy it into your own mail, send it, then mark it as sent
        so the reply deadline starts.
      </Explainer>

      {error && <p style={{ marginTop: 12 }} role="alert"><span className="pill fail">error</span> {error}</p>}

      {sent ? (
        <div style={{ marginTop: 14 }}>
          <p className="row" style={{ gap: 8, flexWrap: "wrap" }}>
            <span className="pill info">sent</span>
            <span style={{ fontSize: 14 }}>
              by {job.outreach_sent_by} on {day(job.outreach_sent_at)}. Reply due {day(job.outreach_reply_due)}.
            </span>
            {job.outreach_overdue && <span className="pill fail">reply overdue</span>}
          </p>
          {job.outreach_overdue && (
            <p className="muted" style={{ fontSize: 13, marginTop: 6 }}>
              The vendor has not answered by the due date. Chase them or escalate.
            </p>
          )}
        </div>
      ) : null}

      {draft ? (
        <div style={{ marginTop: 14 }} className="stack">
          <div>
            <div className="muted" style={{ fontSize: 12 }}>Subject</div>
            <div className="mono" style={{ fontSize: 14 }}>{draft.subject}</div>
          </div>
          <div>
            <div className="muted" style={{ fontSize: 12 }}>Body</div>
            <pre className="mono" style={{ whiteSpace: "pre-wrap", fontSize: 13, margin: 0 }}>{draft.body}</pre>
          </div>
          <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
            <button className="ghost" onClick={onCopy} aria-live="polite">
              <span className="row">{copied ? <Check size={16} /> : <Copy size={16} />} {copied ? "Copied" : "Copy subject and body"}</span>
            </button>
            {!sent && (
              <RoleButton role="engineer" me={me} onClick={onSent} disabled={busy}>
                <span className="row"><Send size={16} /> Mark as sent</span>
              </RoleButton>
            )}
          </div>
        </div>
      ) : (
        <div style={{ marginTop: 14 }}>
          <RoleButton role="engineer" me={me} onClick={onDraft} disabled={busy}>
            {busy ? "Drafting..." : sent ? "Show the enquiry again" : "Draft enquiry"}
          </RoleButton>
        </div>
      )}
    </div>
  );
}

/**
 * The enquiry card with its calls. `onJob` receives the job after the send is recorded. Render it
 * with `key={job.id}`, so a draft made for one job is never shown on another.
 */
export function Outreach({ job, me, onJob }: { job: UC5Job; me: Me | null | undefined; onJob: (j: UC5Job) => void }) {
  const [draft, setDraft] = useState<OutreachDraft | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  async function run(step: () => Promise<void>) {
    setBusy(true); setError(null);
    try { await step(); } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  }

  function copy() {
    if (!draft) return;
    copyDraft(draft).then(
      () => setCopied(true),
      () => setError("The browser refused the clipboard. Select the text and copy it by hand."),
    );
  }

  return (
    <OutreachView
      job={job} me={me} draft={draft} busy={busy} error={error} copied={copied}
      onDraft={() => run(async () => { setDraft(await draftOutreach(job.id)); setCopied(false); })}
      onCopy={copy}
      onSent={() => run(async () => { onJob(await markOutreachSent(job.id)); })}
    />
  );
}
